import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Truck, MapPin, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { createApiUrl } from '../services/shareLinkService';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

const REFRESH_MS = 30000;

const STEPS = [
  { key: 'planned', label: 'Termin geplant' },
  { key: 'on_the_way', label: 'Unterwegs' },
  { key: 'arrived', label: 'Angekommen' },
];

const stepIndex = (status) => (status === 'arrived' ? 2 : status === 'on_the_way' ? 1 : 0);

/** Öffentliche Sendungsverfolgung für Kunden (/track/:token), Daten vom Tracking-Job. */
const TrackingPage = ({ token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const mapEl = useRef(null);
  const mapState = useRef({ map: null, vehicle: null, destination: null, fitted: false });

  const load = useCallback(async () => {
    try {
      const res = await fetch(createApiUrl(`api/public/tracking/${token}`));
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Sendung nicht gefunden');
      setData(body);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Karte: Ziel + (sofern freigegeben) Fahrzeug
  useEffect(() => {
    if (!data?.destination?.lat || !mapEl.current) return;
    loadGoogleMaps(['geometry']).then((maps) => {
      const s = mapState.current;
      if (!s.map) {
        s.map = new maps.Map(mapEl.current, { center: data.destination, zoom: 13, disableDefaultUI: true, zoomControl: true });
        s.destination = new maps.Marker({ position: data.destination, map: s.map, title: 'Ihre Adresse' });
      }
      if (data.vehicle) {
        if (!s.vehicle) {
          s.vehicle = new maps.Marker({
            map: s.map,
            title: 'Unser Fahrzeug',
            icon: { path: maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#2563eb', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3 },
          });
        }
        s.vehicle.setPosition(data.vehicle);
        if (!s.fitted) {
          const bounds = new maps.LatLngBounds();
          bounds.extend(data.vehicle);
          bounds.extend(data.destination);
          s.map.fitBounds(bounds, 60);
          s.fitted = true;
        }
      } else if (s.vehicle) {
        s.vehicle.setMap(null);
        s.vehicle = null;
      }
    }).catch(() => {});
  }, [data]);

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="mx-auto mb-3 text-amber-500" />
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const arrived = data.status === 'arrived';
  const delayed = data.status === 'delay';
  const current = stepIndex(data.status);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
          <Truck className="text-blue-600" />
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Sendungsverfolgung</p>
            <h1 className="font-semibold text-gray-900">{data.company}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-5 space-y-4">
        <section className={`rounded-xl p-5 border ${arrived ? 'bg-green-50 border-green-200' : delayed ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            {arrived ? <CheckCircle2 size={18} className="text-green-600" /> : delayed ? <AlertTriangle size={18} className="text-amber-600" /> : <Truck size={18} className="text-blue-600" />}
            <span className={arrived ? 'text-green-800' : delayed ? 'text-amber-800' : 'text-blue-800'}>
              {arrived ? 'Unser Team ist angekommen' : delayed ? 'Leichte Verspätung' : 'Unser Team ist unterwegs zu Ihnen'}
            </span>
          </div>
          {!arrived && (
            <>
              <p className="mt-3 text-sm text-gray-600">Voraussichtliche Ankunft</p>
              <p className="text-3xl font-bold text-gray-900 tabular-nums">{data.etaFrom} – {data.etaTo} Uhr</p>
              {data.liveEta && (
                <p className="mt-1 text-sm text-gray-600 inline-flex items-center gap-1">
                  <Clock size={14} /> Aktuelle Schätzung: ca. {data.liveEta} Uhr
                </p>
              )}
            </>
          )}
        </section>

        <ol className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3" aria-label="Status">
          {STEPS.map((step, index) => (
            <li key={step.key} className="flex-1 flex flex-col items-center text-center">
              <span className={`w-3 h-3 rounded-full ${index <= current ? 'bg-blue-600' : 'bg-gray-300'}`} />
              <span className={`mt-1 text-xs ${index === current ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{step.label}</span>
            </li>
          ))}
        </ol>

        {data.destination?.lat ? (
          <div ref={mapEl} className="w-full h-72 rounded-xl bg-gray-200 border border-gray-200" aria-label="Karte" />
        ) : null}
        {!data.vehicle && !arrived && (
          <p className="text-xs text-gray-500">Die Fahrzeugposition wird angezeigt, sobald unser Team sich auf den Weg gemacht hat.</p>
        )}

        <section className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-700 space-y-1">
          <p className="inline-flex items-start gap-2"><MapPin size={16} className="mt-0.5 text-gray-400" />{data.destination?.address}</p>
          {data.scheduledStart && <p className="text-gray-500">Geplanter Termin: {data.scheduledStart} Uhr</p>}
          <p className="text-xs text-gray-400">Aktualisiert sich automatisch · Stand {new Date(data.updatedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</p>
        </section>
      </main>
    </div>
  );
};

export default TrackingPage;
